import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import type { SanityClient } from '@sanity/client'

import { indexes } from '@mab-common/utils'

import ContainerDivider from '~/components/atoms/ContainerDivider/ContainerDivider'
import PageHeader from '~/components/atoms/PageHeader/PageHeader'
import { TextSizes } from '~/components/atoms/Text/Text'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import type { AdvantagesBannerProps } from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import AdvantagesBanner from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import ArtistProfileList from '~/components/blocks/ArtistProfileList/ArtistProfileList'
import ArtworksList from '~/components/blocks/ArtworksList/ArtworksList'
import GenreSlider from '~/components/blocks/GenreSlider/GenreSlider'
import InPageNav from '~/components/blocks/InPageNav/InPageNav'
import { useSettingsContext } from '~/contexts/settingsContext'
import { BannerTheme } from '~/enums/banner-theme'
import {
  advantagesBanner,
  internationalizedArrayOfStringsField,
} from '~/schemas/common'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { PushInteractionLocation } from '~/utils/posthogEvents'

import type { PageTemplateModule, TemplateParams } from './types'

import RecordTypes = indexes.RecordTypes

const artworksListEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDsAfterSearch,
    name: SearchAnalystEventNames.artworksArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.artworksArtworkArtistClicked,
  },
  filter: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.artworksArtworkFilterClicked,
  },
}

interface ArtworksTemplateData {
  pageSettings: {
    advantagesBanner: AdvantagesBannerProps
    artworkPageSummary: string
  }
}

/**
 * Artworks Template Component
 * Renders the artworks index page with buy/sell sections
 */
function ArtworksTemplate({ data }: { data: any }) {
  const { pageSettings, breadcrumb } = data
  const router = useRouter()
  const tAll = useTranslations()

  // Building up the jump links
  const jumpToLinks = [
    {
      title: tAll('common.buy'),
      slug: 'buy',
    },
    {
      title: tAll('common.sell'),
      slug: 'sell',
    },
  ]
  const { featuredArtists, totalArtists, genres } = useSettingsContext()

  return (
    <>
      <PageHeader
        title={tAll('common.artForSale')}
        summary={pageSettings.artworkPageSummary}
      />
      <InPageNav items={jumpToLinks} title="Artworks" />

      <VerticalPadder noPaddingBottom={true}>
        <ArtistProfileList
          artists={featuredArtists ? featuredArtists : []}
          totalArtists={totalArtists ? totalArtists : 0}
          totalArtistsTitle="Other notable artists"
          allBtnHandler={() => router.push('/artists')}
          titleSize={TextSizes.primaryTitleMed}
          title={tAll('navigation.artworksTab.featuredArtists')}
          recordType={RecordTypes.ARTIST}
          searchInsightsEvent={{
            method: SearchAnalystMethods.clickedFilters,
            name: SearchAnalystEventNames.artworksArtistClicked,
          }}
          ariaControlId="artist-list"
          showViewAll={true}
        />
      </VerticalPadder>

      <div id="buyContainer">
        <ArtworksList
          pageSize={12}
          sectionId="buy"
          searchPlaceholder={tAll('common.searchArtistOrArtworkTitle')}
          searchInsightsEvents={artworksListEvents}
          hideTradingFloor
          hideFilters={['format']}
          slugParams={false}
        />
      </div>

      {genres && (
        <>
          <ContainerDivider />
          <VerticalPadder>
            <GenreSlider
              genres={genres}
              searchInsightsEvent={{
                method: SearchAnalystMethods.clickedFilters,
                name: SearchAnalystEventNames.homeGenreClicked,
              }}
              title={tAll('navigation.discover')}
            />
          </VerticalPadder>
        </>
      )}

      <div id="sell-container">
        <AdvantagesBanner
          {...pageSettings.advantagesBanner}
          theme={BannerTheme.offwhite}
          analyticsInteractionLocation={PushInteractionLocation.ArtworksPage}
          analyticsCtaSource="ArtworksPage"
        />
      </div>
    </>
  )
}

/**
 * Artworks Page Template Module
 * Fetches settings data with advantagesBanner and artworkPageSummary
 */
export const artworksTemplate: PageTemplateModule<ArtworksTemplateData> = {
  async fetchData(client: SanityClient, params: TemplateParams) {
    const { lang } = params

    const data = await client.fetch<ArtworksTemplateData>(
      `{
        "pageSettings": *[_type == 'settings'][0] {
          ${advantagesBanner},
          "artworkPageSummary": ${internationalizedArrayOfStringsField('artworkPageSummary')}
        }
      }`,
      { lang }
    )

    return data
  },

  component: ArtworksTemplate,
}
